/**
 * Transcript Cache Utility
 *
 * Transcripts are immutable - once generated, they never change.
 * This utility caches transcripts in localStorage to avoid unnecessary API calls.
 */

const CACHE_PREFIX = 'transcript_cache_';
const CACHE_VERSION = 'v1';
const MAX_CACHE_SIZE_MB = 4; // Maximum cache size in MB (browser localStorage limit is ~5MB)
const MAX_CACHE_AGE_DAYS = 30; // Cache expiry in days

/**
 * Generate cache key for a video
 * @param {string} videoId - Video UUID
 * @returns {string} Cache key
 */
const getCacheKey = videoId => {
  return `${CACHE_PREFIX}${CACHE_VERSION}_${videoId}`;
};

/**
 * Get cached transcript for a video
 * @param {string} videoId - Video UUID
 * @returns {Object|null} Cached transcript data or null if not found/expired
 */
export const getCachedTranscript = videoId => {
  try {
    const cacheKey = getCacheKey(videoId);
    const cachedData = localStorage.getItem(cacheKey);

    if (!cachedData) {
      return null;
    }

    const parsed = JSON.parse(cachedData);

    // Check if cache has expired
    const now = Date.now();
    const cacheAge = now - parsed.timestamp;
    const maxAge = MAX_CACHE_AGE_DAYS * 24 * 60 * 60 * 1000; // Convert days to milliseconds

    if (cacheAge > maxAge) {
      // Cache expired, remove it
      localStorage.removeItem(cacheKey);
      return null;
    }

    // Check version match
    if (parsed.version !== CACHE_VERSION) {
      localStorage.removeItem(cacheKey);
      return null;
    }

    return parsed.data;
  } catch (error) {
    console.error('Error reading transcript cache:', error);
    return null;
  }
};

/**
 * Save transcript to cache
 * @param {string} videoId - Video UUID
 * @param {Object} transcriptData - Transcript data to cache
 * @returns {boolean} Success status
 */
export const setCachedTranscript = (videoId, transcriptData) => {
  try {
    const cacheKey = getCacheKey(videoId);
    const cacheData = {
      version: CACHE_VERSION,
      timestamp: Date.now(),
      videoId: videoId,
      data: transcriptData,
    };

    const serialized = JSON.stringify(cacheData);

    // Check size before saving
    const sizeInMB = new Blob([serialized]).size / (1024 * 1024);
    if (sizeInMB > MAX_CACHE_SIZE_MB) {
      console.warn(
        `Transcript too large to cache (${sizeInMB.toFixed(
          2,
        )}MB > ${MAX_CACHE_SIZE_MB}MB)`,
      );
      return false;
    }

    localStorage.setItem(cacheKey, serialized);
    return true;
  } catch (error) {
    // Handle quota exceeded error
    if (error.name === 'QuotaExceededError') {
      console.warn('localStorage quota exceeded, clearing old transcript caches');
      clearOldTranscriptCaches();

      // Try again after clearing
      try {
        const cacheKey = getCacheKey(videoId);
        const cacheData = {
          version: CACHE_VERSION,
          timestamp: Date.now(),
          videoId: videoId,
          data: transcriptData,
        };
        localStorage.setItem(cacheKey, JSON.stringify(cacheData));
        return true;
      } catch (retryError) {
        console.error('Failed to cache transcript after cleanup:', retryError);
        return false;
      }
    }

    console.error('Error caching transcript:', error);
    return false;
  }
};

/**
 * Clear cache for a specific video
 * @param {string} videoId - Video UUID
 */
export const clearCachedTranscript = videoId => {
  try {
    const cacheKey = getCacheKey(videoId);
    localStorage.removeItem(cacheKey);
  } catch (error) {
    console.error('Error clearing transcript cache:', error);
  }
};

/**
 * Clear all transcript caches
 */
export const clearAllTranscriptCaches = () => {
  try {
    const keysToRemove = [];

    // Find all transcript cache keys
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && key.startsWith(CACHE_PREFIX)) {
        keysToRemove.push(key);
      }
    }

    // Remove them
    keysToRemove.forEach(key => localStorage.removeItem(key));

    return keysToRemove.length;
  } catch (error) {
    console.error('Error clearing all transcript caches:', error);
    return 0;
  }
};

/**
 * Clear old transcript caches (older than MAX_CACHE_AGE_DAYS)
 */
const clearOldTranscriptCaches = () => {
  try {
    const now = Date.now();
    const maxAge = MAX_CACHE_AGE_DAYS * 24 * 60 * 60 * 1000;
    const keysToRemove = [];

    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && key.startsWith(CACHE_PREFIX)) {
        try {
          const data = JSON.parse(localStorage.getItem(key));
          const age = now - data.timestamp;

          if (age > maxAge || data.version !== CACHE_VERSION) {
            keysToRemove.push(key);
          }
        } catch (e) {
          // Invalid data, remove it
          keysToRemove.push(key);
        }
      }
    }

    keysToRemove.forEach(key => localStorage.removeItem(key));

    return keysToRemove.length;
  } catch (error) {
    console.error('Error clearing old transcript caches:', error);
    return 0;
  }
};

/**
 * Get cache statistics
 * @returns {Object} Cache stats
 */
export const getTranscriptCacheStats = () => {
  try {
    const stats = {
      count: 0,
      totalSizeBytes: 0,
      oldestTimestamp: null,
      newestTimestamp: null,
    };

    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && key.startsWith(CACHE_PREFIX)) {
        stats.count++;

        const item = localStorage.getItem(key);
        stats.totalSizeBytes += new Blob([item]).size;

        try {
          const data = JSON.parse(item);
          if (!stats.oldestTimestamp || data.timestamp < stats.oldestTimestamp) {
            stats.oldestTimestamp = data.timestamp;
          }
          if (!stats.newestTimestamp || data.timestamp > stats.newestTimestamp) {
            stats.newestTimestamp = data.timestamp;
          }
        } catch (e) {
          // Skip invalid entries
        }
      }
    }

    stats.totalSizeMB = (stats.totalSizeBytes / (1024 * 1024)).toFixed(2);

    return stats;
  } catch (error) {
    console.error('Error getting cache stats:', error);
    return null;
  }
};

/**
 * Check if transcript is cached
 * @param {string} videoId - Video UUID
 * @returns {boolean} True if cached and valid
 */
export const isTranscriptCached = videoId => {
  return getCachedTranscript(videoId) !== null;
};
